// It contains data of stored pages on listing page
// when user changes a page on listing page by e. g. clicking on paginator, changing filter, changing sorting, etc.,
// data for the new page are downloaded using this reducer and data are stored to listingPage.pages array.
// listingPage.pages is a storage of all listing pages which user visits by changing number of page, filtering, sorting, etc.
// listingPage.pages works as a browser cache so when user returns to a page which data were previously downloaded,
// it doesn't download new data but uses page data from listingPage.pages if cache timeout hasn't elapsed yet for the page
// listingPage.pages cache timout is defined in BROWSER_PAGES_CACHE_TIMEOUT constant
import * as log from 'loglevel';
import { createAction, createReducer } from '@reduxjs/toolkit';

import {
  fetchProductsChannelAvailabilities,
  fetchProductsStockAvailability,
  fetchProductsListingPageData,
  fetchSponsoredProductsForSegment
} from '../dataFetcher/dataFetcher';

import { setPendingProductsRequestAndErrorMessage,
  setFiltersIdsTriggeredProductsRequest } from './listingPageCommonData.reduck';

import { createListingPageUrlFromAttributes } from '../listingPageUrlParser/listingPageUrlParser';
import ProductAvailabilityType from './helpers/productavailabilitytype';
import { getTranslation } from '../translations/translations';
import {
  getActiveListingPage,
  getCurrentSetLocationDescriptor,
  getNumberOfActiveFiltersOnActiveListingPage
} from './selectors';
import {
  setFetchingPageDataFailedAnalytics,
  setPageAnalyticsInDigitalData,
  setProductsAnalyticsInDigitalData,
  setProductsPlaceAvailabilitiesAnalytics, setSponsoredProductsCountAnalytics, setSponsoredProductsDataAnalytics
} from '../analytics/pageAndProductsAnalytics';

import UISettings from '../settings/UISettings';
import convertSponsoredProducts from '../dataConverters/sponsoredProductsConverter';
import { convertProductsDataToReduxFormat } from '../dataConverters/productsDataConverter';
import Configuration from '../../../configuration';

const dataLayerUtils = require('@dc/data-layer/dist/utils.js');

const BROWSER_PAGES_CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes


// 1. Initial state
const initialState = [];


// 2. Basic actions creators
export const fetchSponsoredProducts = createAction('listingPage/pages/fetchSponsoredProducts');
export const fetchSponsoredProductsFulfilled = createAction('listingPage/pages/fetchSponsoredProductsFulfilled');
export const fetchSponsoredProductsRejected = createAction('listingPage/pages/fetchSponsoredProductsRejected');
export const storeListingPageXhrResponse = createAction('listingPage/pages/storeListingPageXhrResponse');
const storeReFetchedProductsForSearchLabsABTest =
  createAction('listingPage/pages/storeReFetchedProductsForSearchLabsABTest');
export const removeStoredPage = createAction('listingPage/pages/removeStoredPage');
export const setActiveStoredPage = createAction('listingPage/pages/setActiveStoredPage');
const setStockAvailabilitiesForPage = createAction('listingPage/pages/setStockAvailabilitiesForPage');
const setProductsPlaceAvailabilities = createAction('listingPage/pages/setProductsPlaceAvailabilities');
export const removeAllProductDeliveriesSearchAndAllPlaceSpecificAvailabilities =
  createAction('listingPage/pages/removeAllProductDeliveriesSearchAndAllPlaceSpecificAvailabilities');
const setProductsDeliveriesSearch = createAction('listingPage/pages/setProductsDeliveriesSearch');


// 3. Complex actions which call basic actions above

// called e. g. when user clicks on page on paginator
export function changePageNumber(newPageNumber) {
  return async (dispatch, getState) => {
    if (getState().listingPage.commonData.xhrStatus.pendingProductsRequest) {
      // only one request for products can run at time
      return;
    }

    const currentActivePage = getActiveListingPage(getState());

    const nextPageAttributes = {

      ...currentActivePage.pageAttributes,
      page: newPageNumber
    };

    await getListingPageDataForNextPage(dispatch, getState, nextPageAttributes, currentActivePage);
  };
}

// called e. g. when user clicks on ItemsPerPage component
export function changeNumberOfResultsPerPage(resultsPerPage) {
  return async (dispatch, getState) => {
    if (getState().listingPage.commonData.xhrStatus.pendingProductsRequest) {
      // only one request for products can run at time
      return;
    }

    const currentActivePage = getActiveListingPage(getState());

    const nextPageAttributes = {

      ...currentActivePage.pageAttributes,
      m_results_per_page: resultsPerPage,
      page: '1' // After change of results per page, first page is always shown

    };

    await getListingPageDataForNextPage(dispatch, getState, nextPageAttributes, currentActivePage);
  };
}

// called e. g. when user change sorting type by SortBy component
export function changeSortingOfPage(newSortingOption) {
  return async (dispatch, getState) => {
    if (getState().listingPage.commonData.xhrStatus.pendingProductsRequest) {
      // only one request for products can run at time
      return;
    }

    const currentActivePage = getActiveListingPage(getState());

    const nextPageAttributes = {

      ...currentActivePage.pageAttributes,
      sort: newSortingOption,
      page: '1' // After change of sorting, first page is always shown

    };

    await getListingPageDataForNextPage(dispatch, getState, nextPageAttributes, currentActivePage);
  };
}

// called e. g. when user changes something in price filter
export function changePriceFilter(priceMin, priceMax) {
  return async (dispatch, getState) => {
    if (getState().listingPage.commonData.xhrStatus.pendingProductsRequest) {
      // only one request for products can run at time
      return;
    }

    let segmentFilterExist = false;
    let segmentFilterLabel = '';
    let segmentFilterId = '';
    const currentActivePage = getActiveListingPage(getState());
    const sLFilters = currentActivePage.filtersFromSearchLabs;

    if (sLFilters.length > 0 && sLFilters[0].id === 'segment_id') {
      sLFilters[0].values.map((val) => {
        if (val.selected === true) {
          segmentFilterExist = true;
          segmentFilterLabel = val.label;
          segmentFilterId = val.value;
        }
        return null;
      });
    }
    const segmentFilter = segmentFilterExist
      ? {
        filterId: segmentFilterId,
        label: segmentFilterLabel
      }
      : null;

    const nextPageAttributes = {

      ...currentActivePage.pageAttributes,
      price_min: typeof priceMin !== 'undefined' ? String(priceMin) : undefined,
      price_max: typeof priceMax !== 'undefined' ? String(priceMax) : undefined,
      page: '1', // After change of price filter, first page is always shown
      segmentFilter: segmentFilter

    };

    await getListingPageDataForNextPage(dispatch, getState, nextPageAttributes, currentActivePage, ['price']);
  };
}

// called e. g. when user changes any filter
export function changeRegularFilter(filterId, newValues) {
  return async (dispatch, getState) => {
    if (getState().listingPage.commonData.xhrStatus.pendingProductsRequest) {
      // only one request for products can run at time
      return;
    }

    // rounded_score has different format of newValues
    if (filterId === 'rounded_score' && typeof newValues !== 'undefined') {
      // eslint-disable-next-line no-param-reassign
      newValues = newValues[0].split(',');
    }

    const currentActivePage = getActiveListingPage(getState());

    const nextPageAttributes = {

      ...currentActivePage.pageAttributes,
      filterAttributes: {
        ...currentActivePage.pageAttributes.filterAttributes,
        [filterId]: newValues
      },
      page: '1' // After change of regular filter, first page is always shown

    };

    await getListingPageDataForNextPage(dispatch, getState, nextPageAttributes, currentActivePage, [filterId]);
  };
}

// called e. g. when user clicks on 'Reset filters' link
export function resetAllFilters() {
  return async (dispatch, getState) => {
    if (getState().listingPage.commonData.xhrStatus.pendingProductsRequest) {
      // only one request for products can run at time
      return;
    }

    const currentActivePage = getActiveListingPage(getState());

    const nextPageAttributes = { ...currentActivePage.pageAttributes };

    // for showing loaders above resetting filters
    const currentActiveFiltersIds = [];

    if (currentActivePage.pageAttributes.price_min) {
      // price filter is active
      currentActiveFiltersIds.push('price');
    }

    // add to currentActiveFiltersIds price filter of active price filters from SearchLabs
    currentActivePage.filtersFromSearchLabs.forEach((filterItem) => {
      filterItem.values.forEach((valueItem) => {
        if (valueItem.selected) {
          currentActiveFiltersIds.push(filterItem.id);
        }
      });
    });

    // reset filter price range
    delete nextPageAttributes.price_min;
    delete nextPageAttributes.price_max;

    // After reset of filters, first page is always shown
    nextPageAttributes.page = '1';

    nextPageAttributes.filterAttributes = {
      // category, market and segment IDs have to be kept in filters attributes for segment listing page even if filters are reset
      ...(nextPageAttributes.filterAttributes.category_id &&
        { category_id: nextPageAttributes.filterAttributes.category_id }),
      ...(nextPageAttributes.filterAttributes.market_id &&
        { market_id: nextPageAttributes.filterAttributes.market_id }),
      ...(nextPageAttributes.filterAttributes.segment_id &&
        !currentActivePage.searchListingPage &&
        { segment_id: nextPageAttributes.filterAttributes.segment_id }),

      // tag, id and externalref filters have to be kept in filters attributes for search listing page even if filters are reset
      ...(nextPageAttributes.filterAttributes.tag &&
        currentActivePage.searchListingPage &&
        { tag: nextPageAttributes.filterAttributes.tag }),
      ...(nextPageAttributes.filterAttributes.id &&
        currentActivePage.searchListingPage &&
        { id: nextPageAttributes.filterAttributes.id }),
      ...(nextPageAttributes.filterAttributes.externalref &&
        currentActivePage.searchListingPage &&
        { externalref: nextPageAttributes.filterAttributes.externalref })
    };

    await getListingPageDataForNextPage(dispatch, getState, nextPageAttributes, currentActivePage, currentActiveFiltersIds);
  };
}

// called e. g. when user removes a filter from Active filters panel
export function removeSingleValueFromFilter(
  filterTypeId, filterValueToBeRemovedId, filterTypeLabel, filterValueToBeRemovedLabel) {
  return async (dispatch, getState) => {
    if (getState().listingPage.commonData.xhrStatus.pendingProductsRequest) {
      // only one request for products can run at time
      return;
    }

    const currentActivePage = getActiveListingPage(getState());

    let nextPageAttributes;

    if (filterTypeId === 'price') {
      nextPageAttributes = {

        ...currentActivePage.pageAttributes,
        price_min: undefined,
        price_max: undefined,
        page: '1' // After removing of price filter, first page is always shown

      };
    } else if (filterTypeId === 'rounded_score') {
      nextPageAttributes = {

        ...currentActivePage.pageAttributes,
        filterAttributes: {
          ...currentActivePage.pageAttributes.filterAttributes,
          rounded_score: undefined
        },
        page: '1' // After removing of single filter value, first page is always shown
      };

    } else {
      // remove the filter value from array of values for the filterId
      const newFilterValuesArray = currentActivePage.pageAttributes.filterAttributes[filterTypeId].filter(
        (filterValue) => filterValue !== filterValueToBeRemovedId
      );

      nextPageAttributes = {

        ...currentActivePage.pageAttributes,
        filterAttributes: {
          ...currentActivePage.pageAttributes.filterAttributes,
          [filterTypeId]: newFilterValuesArray.length > 0 ? newFilterValuesArray : undefined
        },
        page: '1', // After removing of single filter value, first page is always shown
        segmentFilter: null
      };
    }


    // 'removedFilter' attribute is added here only for analytics purpose to track when users remove active filter
    const dataForAnalytics = {
      removedFilter: {
        filterTypeLabel,
        filterValueLabel: filterValueToBeRemovedLabel
      }
    };

    await getListingPageDataForNextPage(
      dispatch, getState, nextPageAttributes, currentActivePage, [filterTypeId], true, dataForAnalytics
    );
  };
}

// called e. g. when user clicks on back or forward button in the web browser
export function getPageDataAfterChangingPageUsingBrowserHistory(
  nextPageAttributes, currentActivePage, triggeredFiltersIds = undefined) {
  return async (dispatch, getState) => {
    await getListingPageDataForNextPage(
      dispatch, getState, nextPageAttributes, currentActivePage, triggeredFiltersIds, false);
  };
}


// This function is called when user wants to show a new page by calling any method from above (when user changed page, sorting,
// filtering, etc.)
// The function does following:
// - the function tries to get page from cache in redux store in listingPage.pages array.
// - if page is in cache and not timed out, data from cache is used, otherwise the data are gathered using web services
// - gathers channels availabilities, if a location is selected and if needed
// - converts and stores the page data to pages.listingPage (only uncached page)
// - set the new page as active (set listingPage.pages[newPageIndex].isActive = true)
// - set the URL of new page in browser URL bar if it wasn't changed by clicking back/forward buttons in web browser
// - run analytics for new page (change/add data to window.digitalData using @dc/data-layer)
//
// - handles all errors which happens during this process
// if an error occurs, the previous page keeps active in browser and an error message is displayed in GUI
async function getListingPageDataForNextPage(
  dispatch, getState, nextPageAttributes, currentActivePage,
  triggeredFiltersIds = undefined, setUrlInBrowser = true, dataForAnalytics = {}) {
  // create URL suffix of the next page,
  // URL suffix uniquely identifies each page
  const nextPageUrlSuffix = createListingPageUrlFromAttributes(nextPageAttributes);

  if (currentActivePage && currentActivePage.pageUrlSuffix === nextPageUrlSuffix) {
    // the next page is the same as the current page - no change required;
    return;
  }

  dispatch(setPendingProductsRequestAndErrorMessage({
    isPending: true,
    errorMessage: null
  }));

  // trying to get the next page from cache from listingPage.pages
  const cachedPage = getState().listingPage.pages.find((page) => page.pageUrlSuffix === nextPageUrlSuffix);

  if (cachedPage) {
    // next page is already in cache
    if (new Date() - cachedPage.timeFetched < BROWSER_PAGES_CACHE_TIMEOUT) {
      // cached page didn't timeout so it is used as the current page
      dispatch(setActiveStoredPage(nextPageUrlSuffix));

      // we set URL in browser when user goes to a different page by action in GUI
      // if user clicks back or forward button in the web browser, we don't change the URL,
      // because it's automatically set by the web browser
      if (setUrlInBrowser) {
        // set correct url in browser
        window.history.pushState(
          // this data are stored here because they are used for performing AJAX call instead of page reload
          // when user clicks on back or forward button in the web browser
          {
            nextPageAttributes, currentActivePage, triggeredFiltersIds
          },
          '',
          cachedPage.pageUrlPrefix + cachedPage.pageUrlSuffix);
      }


      // Check if new channels availabilites for a location has to be gathered for cached page
      if (getCurrentSetLocationDescriptor(getState()) &&
          getCurrentSetLocationDescriptor(getState()).id !==
          cachedPage.productsDeliveriesSearch.locationId) {
        // Availabilities has to be gathered for the cached page because some other cached page has location set
        // And the cached page doesn't have the same location set as the other one
        const productIds = cachedPage.productList.map((product) => product.id);

        dispatch(searchDeliveriesForLocation(
          getCurrentSetLocationDescriptor(getState()), productIds, cachedPage.pageUrlSuffix)
        );
      }
      // The timeout is here due to allow scrolling after using page data from cache
      // because data from cache needs to be rendered before scrolling
      setTimeout(() => {
        dispatch(setPendingProductsRequestAndErrorMessage({
          isPending: false,
          errorMessage: null
        }));
      }, 0);

      // set analytics data in digitalData object
      dispatch(setPageAnalyticsInDigitalData(true, dataForAnalytics));
      dispatch(setProductsAnalyticsInDigitalData());
      // analytics for sponsored products
      if (cachedPage.sponsoredProducts && Array.isArray(cachedPage.sponsoredProducts.items) &&
          cachedPage.sponsoredProducts.items.length > 0) {
        dispatch(setSponsoredProductsCountAnalytics(cachedPage.sponsoredProducts.items.length));
        dispatch(setSponsoredProductsDataAnalytics(cachedPage.sponsoredProducts.items));
      }

      // cached page is used so we can finish
      return;
    }
    // cached page timed out so remove it from store and continue
    dispatch(removeStoredPage(cachedPage.pageUrlSuffix));

  }

  // Next page is not in cache or it is timed out, so we have to load data for it:

  // if gathering data for new page was initiated by changing of filters we have to set which filters it is
  // because it's required by filters components for showing spinner
  if (triggeredFiltersIds) {
    dispatch(setFiltersIdsTriggeredProductsRequest(triggeredFiltersIds));
  }
  const fetchDataResult = await fetchProductsListingPageData(nextPageAttributes);

  // fetch data failed
  if (!fetchDataResult.ok) {
    dispatch(setPendingProductsRequestAndErrorMessage({
      isPending: false,
      errorMessage: fetchDataResult.customErrorMessage
    }));
    if (triggeredFiltersIds) {
      dispatch(setFiltersIdsTriggeredProductsRequest([]));
    }
    logErrorAfterFetchFailed(fetchDataResult);

    dispatch(setFetchingPageDataFailedAnalytics(fetchDataResult));

    return;
  }

  // when fetchDataResult contains redirectUrl,
  // we have to redirect to the page stored in the redirectUrl property
  if (fetchDataResult.redirectUrl) {
    window.location.href = fetchDataResult.redirectUrl;

    return;
  }

  const conversionResult = convertFetchedDataForListingPageToReduxFormat({
    ...fetchDataResult.data,
    searchLabsRequestAttrs: nextPageAttributes,
    ucmsHierarchy: getState().listingPage.staticData.ucmsHierarchy
  });

  if (!conversionResult.ok) {
    // conversion failed
    dispatch(setPendingProductsRequestAndErrorMessage({
      isPending: false,
      errorMessage: conversionResult.customErrorMessage
    }));
    if (triggeredFiltersIds) {
      dispatch(setFiltersIdsTriggeredProductsRequest([]));
    }
    logErrorAfterFetchFailed(conversionResult);
    dispatch(setFetchingPageDataFailedAnalytics(fetchDataResult));

    return;
  }

  dispatch(storeListingPageXhrResponse(conversionResult.convertedData));
  dispatch(setPendingProductsRequestAndErrorMessage({
    isPending: false,
    errorMessage: null
  }));
  if (triggeredFiltersIds) {
    dispatch(setFiltersIdsTriggeredProductsRequest([]));
  }

  const newActiveListingPage = getActiveListingPage(getState());

  // we set URL in browser when user goes to a different page by action in GUI
  // if user clicks back or forward button in the web browser, we don't change the URL,
  // beacuse it's automatically set by the web browser
  if (setUrlInBrowser) {
    // set correct url in browser
    window.history.pushState(
      // this data are stored here because they are used for performing AJAX call instead of page reload
      // when user clicks on back or forward button in the web browser
      {
        nextPageAttributes, currentActivePage, triggeredFiltersIds
      },
      '',
      newActiveListingPage.pageUrlPrefix + newActiveListingPage.pageUrlSuffix);
  }

  // Check if channels availabilities for a location has to be gathered for the next page
  if (getCurrentSetLocationDescriptor(getState())) {
    // Availabilities has to be gathered for the next page because some other cached page has location set
    const productIds = conversionResult.convertedData.productList.map((product) => product.id);

    dispatch(searchDeliveriesForLocation(
      getCurrentSetLocationDescriptor(getState()), productIds, newActiveListingPage.pageUrlSuffix)
    );
  }
  // set analytics
  dispatch(setPageAnalyticsInDigitalData(false, dataForAnalytics));

  dispatch(searchStockAvailabilityForProductsOnPageAndSetProductsAnalyticsData());


  log.info('[LISTING PAGE DATA] loaded');
}


// THIS FUNCTION IS JUST USED FOR SEARCHLABS A/B TEST:
// When a searchLabs a/b test is active this function refetches data on client
// the new data has to be refetched with query parameters for the ab/test
// after first render and first loading of the page.
// SearchLabs a/b test is active when global window.Search_VisitorSegmentId variable is defined and has a string value
// TODO: Findability team has to change architecture of this a/b test because it has very low architecture
// TODO: Remove this code as soon as Findability changes the architecture of the a/b test
export function reFetchProductsForSearchLabsABTest() {
  return async (dispatch, getState) => {
    // pageAttributes are the same like for the current page
    const pageAttributes = getActiveListingPage(getState()).pageAttributes;

    dispatch(setPendingProductsRequestAndErrorMessage({
      isPending: true,
      errorMessage: null
    }));

    const fetchDataResult = await fetchProductsListingPageData(pageAttributes);

    // fetch data failed
    if (!fetchDataResult.ok) {
      dispatch(setPendingProductsRequestAndErrorMessage({
        isPending: false,
        errorMessage: fetchDataResult.customErrorMessage
      }));

      return;
    }

    // when fetchDataResult contains redirectUrl,
    // we have to redirect to the page stored in the redirectUrl property
    if (fetchDataResult.redirectUrl) {
      window.location.href = fetchDataResult.redirectUrl;

      return;
    }

    const conversionResult = convertFetchedDataForListingPageToReduxFormat({
      ...fetchDataResult.data,
      searchLabsRequestAttrs: pageAttributes,
      ucmsHierarchy: getState().listingPage.staticData.ucmsHierarchy
    });

    if (!conversionResult.ok) {
      // conversion failed
      dispatch(setPendingProductsRequestAndErrorMessage({
        isPending: false,
        errorMessage: conversionResult.customErrorMessage
      }));
      logErrorAfterFetchFailed(conversionResult);

      return;
    }

    dispatch(storeReFetchedProductsForSearchLabsABTest(conversionResult.convertedData));
    dispatch(setPendingProductsRequestAndErrorMessage({
      isPending: false,
      errorMessage: null
    }));

    // Check if channels availabilities for a location has to be gathered for the next page
    if (getCurrentSetLocationDescriptor(getState())) {
      const newActiveListingPage = getActiveListingPage(getState());
      // Availabilities has to be gathered for the next page because some other cached page has location set
      const productIds = conversionResult.convertedData.productList.map((product) => product.id);

      dispatch(searchDeliveriesForLocation(
        getCurrentSetLocationDescriptor(getState()), productIds, newActiveListingPage.pageUrlSuffix)
      );
    }
    // set analytics
    dispatch(setPageAnalyticsInDigitalData(false, {}));

    dispatch(searchStockAvailabilityForProductsOnPageAndSetProductsAnalyticsData());
  };
}

export function convertFetchedDataForListingPageToReduxFormat(dataObject) {
  try {
    return {
      ok: true,
      convertedData: convertProductsDataToReduxFormat(dataObject)
    };
  } catch (err) {
    return {
      ok: false,
      errorObject: err,
      customErrorMessage: `It wasn't possible to convert listing page data to redux format. The error occurred in function 
        convertProductsDataToReduxFormat() located in file /fo-app/src/modules/ListingPage/dataConverters/productsDataConverter.js
        so there can be a bug in the function. The data are gathered from 3 independent sources: UCMS structure gathered from, 
        fo-site API, SearchLabs (${Configuration.get('api.listingPage.searchAndFilter')}) and Store API
        (${Configuration.get('api.listingPage.products')}). It's possible that one of this 3 sources delivered
        a wrong format of data.`,
      errorCode: 500
    };
  }
}

// search channels availabilities for a location selected in Location Search component
export function searchDeliveriesForLocation(resolvedLocationDescriptor, productIds, listingPageUrl) {
  return async (dispatch, getState) => {
    try {

      dispatch(setProductsDeliveriesSearch({
        data: {
          searchPending: true,
          locationId: null,
          errorMessage: ''
        },
        listingPageUrl: listingPageUrl
      }));

      const result = await fetchProductsChannelAvailabilities(productIds, resolvedLocationDescriptor);

      const availabilities = [];
      if (result.status === 'success') {
        result.data.postcodeResults.forEach((postcodeResult) => {
          availabilities.push(new ProductAvailabilityType(
            postcodeResult,
            resolvedLocationDescriptor.postcode));
        });

        const activeListingPage = getActiveListingPage(getState());

        // if availabilities were gathered for non active page, the availabilities are thrown away and not added to UI
        if (activeListingPage.pageUrlSuffix !== listingPageUrl) {
          log.info(`postcode checker response was ignored for page  ${listingPageUrl}, because the page is not active`);
          dispatch(setProductsDeliveriesSearch({
            data: {
              searchPending: false,
              locationId: null,
              errorMessage: ''
            },
            listingPageUrl: listingPageUrl
          }));
          return;
        }

        if (availabilities.length > 0) {
          dispatch(setProductsDeliveriesSearch({
            data: {
              searchPending: false,
              locationId: resolvedLocationDescriptor.id,
              errorMessage: ''
            },
            listingPageUrl: listingPageUrl
          }));

          // add availabilites to page data
          dispatch(setProductsPlaceAvailabilities({
            availabilitiesList: availabilities
          }));

          // Analytics
          dispatch({ type: 'EVENTS_POSTCODE_CHECK_INITIATED', payload: { location: resolvedLocationDescriptor.location } });
          dispatch(setProductsPlaceAvailabilitiesAnalytics(availabilities, activeListingPage));
          dataLayerUtils.satelliteTrack('locationcomplete');

        } else {
          throw new Error('No availabilities in results of web service');
        }
      } else {
        throw new Error(result.data.message);
      }
    } catch (e) {
      if (getActiveListingPage(getState()).pageUrlSuffix === listingPageUrl) {
        let errorMessage = getTranslation('postcodeCheckerSearchingError');
        if (e && e.detail && typeof e.detail.statusText === 'string') {
          errorMessage = e.detail.statusText;
        }
        dispatch(setProductsDeliveriesSearch({
          data: {
            searchPending: false,
            locationId: null,
            errorMessage: errorMessage
          },
          listingPageUrl: listingPageUrl
        }));
      } else {
        dispatch(setProductsDeliveriesSearch({
          data: {
            searchPending: false,
            locationId: null,
            errorMessage: ''
          },
          listingPageUrl: listingPageUrl
        }));

        log.info(`Postcode checker failed for page ${listingPageUrl} but because this page is not active error message is not 
        shown in UI `);
      }

      log.error(`Postcode checker failed for page ${listingPageUrl} due to following reasons: ${e.message}`);
    }
  };
}


// this function is used only once during init of listing page in browser after load/reload page
export function searchStockAvailabilityForProductsOnPageAndSetProductsAnalyticsData(pageUrl) {
  return async (dispatch, getState) => {
    const currentActivePage = getActiveListingPage(getState());
    const productIdsToCheck =
        currentActivePage.productList.filter((product) => product.checkInStock).map((product) => product.id);

    if (productIdsToCheck.length === 0) {
      log.info(`No products for checking stock on page ${pageUrl}`);
      // set analytics data
      dispatch(setProductsAnalyticsInDigitalData());
      return;
    }

    const stockAvailabilitiesFetchResult =
        await fetchProductsStockAvailability(productIdsToCheck);

    if (!stockAvailabilitiesFetchResult.ok) {
      // fetch failed
      logErrorAfterFetchFailed(stockAvailabilitiesFetchResult);

      return;
    }

    log.info(`[STOCK INFO] successfully gathered and parsed for products on page ${currentActivePage.pageUrlSuffix}`);

    dispatch(setStockAvailabilitiesForPage({
      availabilities: stockAvailabilitiesFetchResult.storeStockAPIData.payload,
      pageUrlSuffix: currentActivePage.pageUrlSuffix
    }));

    // set analytics data
    dispatch(setProductsAnalyticsInDigitalData());
  };
}

export function checkAndGetSponsoredProducts() {
  return async (dispatch, getState) => {

    const currentActivePage = getActiveListingPage(getState());
    const numberOfActiveFilters = getNumberOfActiveFiltersOnActiveListingPage(getState());


    if (UISettings.get('component.hooklogic.enabled') &&
      currentActivePage.sortByOptions.items.findIndex((item) => item.value === 'relevance' && item.selected) !== -1 &&
      currentActivePage.pagination.findIndex((item) => item.page === '1' && item.selected) !== -1 &&
      numberOfActiveFilters === 0
    ) {

      dispatch(fetchSponsoredProducts({ pageWithSponsoredProductsUrlSuffix: currentActivePage.pageUrlSuffix }));

      const fetchSponsoredProductsResult = await fetchSponsoredProductsForSegment(
        currentActivePage.pageAttributes.filterAttributes.segment_id[0]
      );

      if (!fetchSponsoredProductsResult.ok) {
        // fetch failed
        dispatch(fetchSponsoredProductsRejected({ pageWithSponsoredProductsUrlSuffix: currentActivePage.pageUrlSuffix }));
        logErrorAfterFetchFailed(fetchSponsoredProductsResult);
      }


      const sponsoredProducts = fetchSponsoredProductsResult.data;
      if (sponsoredProducts.payload.items.length > 0) {
        const convertedProducts = convertSponsoredProducts(sponsoredProducts.payload);
        dispatch(fetchSponsoredProductsFulfilled({
          products: convertedProducts,
          pageWithSponsoredProductsUrlSuffix: currentActivePage.pageUrlSuffix
        }));

        dispatch(setSponsoredProductsCountAnalytics(convertedProducts.items.length));
        dispatch(setSponsoredProductsDataAnalytics(convertedProducts.items));

      } else {
        log.info('No hooklogic products in the hooklogic response ');
        dispatch(setSponsoredProductsCountAnalytics(0));
      }

    } else {
      log.info('No hooklogic data fetch for this page');
    }
  };
}

function logErrorAfterFetchFailed(fetchDataResult) {
  log.error(fetchDataResult.customErrorMessage);
  if (fetchDataResult.errorObject) {
    log.error(fetchDataResult.errorObject.message);
    log.error(fetchDataResult.errorObject.stack);
  }
}


// 4. Reducer
/* eslint-disable no-param-reassign */
export default createReducer(initialState, {
  [storeListingPageXhrResponse]: (stateDraft, action) => {
    stateDraft.forEach((page) => {
      page.isActive = false;
    });

    stateDraft.push({ ...action.payload });
  },
  [storeReFetchedProductsForSearchLabsABTest]: (stateDraft, action) => {
    // removes all pages
    stateDraft.splice(0);

    stateDraft.push({ ...action.payload });
  },
  [removeStoredPage]: (stateDraft, action) => (
    stateDraft.filter((page) => page.pageUrlSuffix !== action.payload)
  ),
  [setActiveStoredPage]: (stateDraft, action) => {
    stateDraft.forEach((page) => {
      page.isActive = page.pageUrlSuffix === action.payload;
    });
  },
  [setProductsDeliveriesSearch]: (stateDraft, action) => {
    stateDraft.find((page) =>
      page.pageUrlSuffix === action.payload.listingPageUrl
    ).productsDeliveriesSearch = action.payload.data;
  },
  [setStockAvailabilitiesForPage]: (stateDraft, action) => {
    const page = stateDraft.find((currentPage) => action.payload.pageUrlSuffix === currentPage.pageUrlSuffix);

    if (!page) {
      log.warn(`The page ${action.payload.pageUrlSuffix} is not stored in the store so stock availabilities` +
        "can't be assigned to the products on the page");
    } else {
      page.productList.forEach((product) => {
        const productStockInfo = action.payload.availabilities.find((avail) => avail.productId === product.id);
        if (productStockInfo) {
          product.stockChecked = true;
          product.inStock = productStockInfo.quantityAvailable > 0;
        }
      });
    }
  },
  [setProductsPlaceAvailabilities]: (stateDraft, action) => {
    stateDraft.forEach((page) => {
      page.productList.forEach((product) => {
        action.payload.availabilitiesList.forEach((productAvailability) => {
          if (product.id === productAvailability.getProductId()) {
            product.placeSpecificChannelsAvailability = {};

            if (productAvailability.isSuccessful()) {
              // eslint-disable-next-line no-param-reassign
              product.placeSpecificChannelsAvailability.delivery = {
                label: productAvailability.getDeliveryLabel(),
                type: productAvailability.isDeliverable()
                  ? 'homeDeliveryAvailable' : 'homeDeliveryUnavailable'
              };
            }

            // If delivery and collection has the same label, store only delivery instead.
            // Ignore collection data.
            if (productAvailability.getCollectionLabel() !==
              productAvailability.getDeliveryLabel()) {
              // eslint-disable-next-line no-param-reassign
              product.placeSpecificChannelsAvailability.inStore = {
                label: productAvailability.getCollectionLabel(),
                type: productAvailability.isCollectable()
                  ? 'collectInStoreAvailable' : 'collectInStoreUnavailable'
              };
            }
          }
        });
      });
    });
  },
  [removeAllProductDeliveriesSearchAndAllPlaceSpecificAvailabilities]: (stateDraft) => {
    stateDraft.forEach((page) => {
      page.productsDeliveriesSearch = {
        searchPending: false,
        locationId: null,
        errorMessage: ''
      };

      page.productList.forEach((product) => {
        delete product.placeSpecificChannelsAvailability;
      });
    });
  },
  [fetchSponsoredProducts]: (stateDraft, action) => {
    stateDraft.find((page) => page.pageUrlSuffix === action.payload.pageWithSponsoredProductsUrlSuffix)
      .sponsoredProductsLoading = true;
  },
  [fetchSponsoredProductsFulfilled]: (stateDraft, action) => {
    const pageWithSponsoredProducts =
      stateDraft.find((page) => page.pageUrlSuffix === action.payload.pageWithSponsoredProductsUrlSuffix);

    pageWithSponsoredProducts.sponsoredProductsLoading = false;
    pageWithSponsoredProducts.sponsoredProducts = action.payload.products;
  },
  [fetchSponsoredProductsRejected]: (stateDraft, action) => {
    stateDraft.find((page) => page.pageUrlSuffix === action.payload.pageWithSponsoredProductsUrlSuffix)
      .sponsoredProductsLoading = false;
  }
});
